<!--
  Kassenabschluss erneut übermitteln (#771).

  Two cards and a list: the filter with its results, the reason with the start button, then the runs. Every
  row reflows instead of switching layout: the filter is one column on a phone and wider from `sm` up, and a
  candidate row stacks its chips below `sm`.
-->
<section class="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-6 sm:gap-6 sm:py-8">
  <header class="flex flex-col gap-1">
    <h1 class="text-2xl font-bold tracking-tight text-gray-900">Kassenabschluss erneut übermitteln</h1>
    <p class="text-sm text-gray-500">
      Wählen Sie bereits übermittelte Kassenabschlüsse aus, die aus den gespeicherten Kassendaten neu erstellt
      und erneut übermittelt werden sollen. Jeder gewählte Abschluss wird dabei zurückgezogen und unter einer
      neuen Kennung übermittelt.
    </p>
  </header>

  <!--
    Only a failed READ lands here: it is not tied to one button, so the top of the page is the only place it
    belongs. The start and retry results render at the action that produced them (Bug #866).
  -->
  @if (message(); as text) {
    <p class="rounded-xl px-4 py-3 text-sm" data-testid="correction-load-error"
       [class.bg-green-50]="messageIsSuccess()"
       [class.text-green-700]="messageIsSuccess()"
       [class.bg-red-50]="!messageIsSuccess()"
       [class.text-red-700]="!messageIsSuccess()"
       [attr.role]="messageIsSuccess() ? 'status' : 'alert'">{{ text }}</p>
  }

  <!-- ── Kassenabschlüsse auswählen ──────────────────────────────────────────────────────────────────── -->
  <section class="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
            data-testid="correction-filter-card">
    <h2 class="text-base font-bold tracking-tight text-gray-900">Kassenabschlüsse auswählen</h2>

    <div class="mt-4 grid grid-cols-2 gap-2 sm:gap-3">
      <!-- min-w-0 + explicit height + appearance-none: a native date input in a grid overflows on iOS (Bug #646). -->
      <div class="min-w-0">
        <label class="mb-1 block text-xs font-medium text-gray-500" for="correction-from">Von</label>
        <input type="date" id="correction-from" [max]="today"
               [ngModel]="from()" (ngModelChange)="from.set($event)"
               class="block h-10 w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-200
                      bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm
                      focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                      [&::-webkit-date-and-time-value]:text-left">
      </div>
      <div class="min-w-0">
        <label class="mb-1 block text-xs font-medium text-gray-500" for="correction-to">Bis</label>
        <input type="date" id="correction-to" [max]="today"
               [ngModel]="to()" (ngModelChange)="to.set($event)"
               class="block h-10 w-full min-w-0 max-w-full appearance-none rounded-lg border border-gray-200
                      bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm
                      focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500
                      [&::-webkit-date-and-time-value]:text-left">
      </div>
    </div>

    <div class="mt-4">
      <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="correction-registers">
        Kassen
      </label>
      <cqme-select id="correction-registers" size="md" [multiple]="true" [clearable]="true"
                   [options]="selectableRegisters()" labelKey="name" valueKey="id"
                   placeholder="Alle Kassen"
                   ariaLabel="Kassen, deren Kassenabschlüsse angezeigt werden"
                   [current]="selectedRegisterIds().length > 0 ? selectedRegisterIds() : undefined"
                   (currentChange)="onRegistersChanged($event)"/>
    </div>

    <div class="mt-4 flex flex-wrap items-center gap-3">
      <button type="button"
              class="inline-flex items-center justify-center rounded-2xl border border-gray-200 px-4 py-2.5
                     text-sm font-semibold text-gray-600 shadow-sm transition-all duration-200
                     hover:border-gray-300 hover:bg-gray-50 active:scale-95
                     disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale"
              [disabled]="!periodValid() || candidatesLoading()" (click)="loadCandidates()">
        {{ candidatesLoading() ? 'Wird geladen …' : 'Kassenabschlüsse anzeigen' }}
      </button>
      @if (!periodComplete()) {
        <span class="text-xs font-semibold text-red-600">
          Bitte geben Sie ein gültiges Von- und Bis-Datum an.
        </span>
      } @else if (!periodValid()) {
        <span class="text-xs font-semibold text-red-600">Das Bis-Datum darf nicht vor dem Von-Datum liegen.</span>
      }
    </div>

    <!-- The results -->
    <div class="mt-5 border-t border-gray-100 pt-4">
      @if (candidatesLoading() && candidates().length === 0) {
        <p class="text-sm text-gray-500">Kassenabschlüsse werden geladen …</p>
      } @else if (candidatesLoaded() && candidates().length === 0) {
        <p class="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
          Im gewählten Zeitraum gibt es keine übermittelten Kassenabschlüsse, die sich erneut übermitteln lassen.
        </p>
      } @else if (candidates().length > 0) {
        <div class="flex flex-wrap items-center justify-between gap-2">
          <cqme-checkbox [id]="'correction-select-all'"
                         [label]="'Alle angezeigten auswählen (' + selectableCandidates().length + ')'"
                         [disabled]="selectableCandidates().length === 0"
                         [checked]="allSelected()" (checkedChange)="toggleAll($event)"/>
          <span class="text-xs font-semibold text-gray-600" aria-live="polite"
                data-testid="correction-selected-count">
            Ausgewählt: {{ selectedCountLabel() }}
          </span>
        </div>

        <ul class="mt-3 flex flex-col gap-2" aria-label="Kassenabschlüsse">
          @for (candidate of candidates(); track candidate.entryId) {
            <li class="flex flex-col gap-1.5 rounded-xl border border-gray-100 px-3 py-2.5
                       sm:flex-row sm:items-center sm:justify-between"
                [class.bg-gray-50]="candidate.correctionOutstanding"
                [attr.data-testid]="'candidate-' + candidate.entryId">
              <div class="min-w-0">
                <cqme-checkbox [id]="'candidate-check-' + candidate.entryId" [label]="title(candidate)"
                               [disabled]="candidate.correctionOutstanding"
                               [checked]="isSelected(candidate)"
                               (checkedChange)="toggleCandidate(candidate, $event)"/>
                <p class="pl-7 text-xs text-gray-600">
                  Abschluss Nr. {{ candidate.closingNumber }}
                  @if (candidate.filings > 1) {
                    · bereits {{ candidate.filings - 1 }}-mal erneut übermittelt
                  }
                </p>
                @if (candidate.correctionOutstanding) {
                  <p class="pl-7 text-xs font-semibold text-gray-700">
                    Für diesen Kassenabschluss läuft bereits eine erneute Übermittlung.
                  </p>
                }
              </div>
              <span class="shrink-0 self-start rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold
                           text-gray-600 sm:self-center">{{ stateLabel(candidate) }}</span>
            </li>
          }
        </ul>
      }
    </div>
  </section>

  <!-- ── Erneut übermitteln ─────────────────────────────────────────────────────────────────────────── -->
  <section class="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6"
            data-testid="correction-start-card">
    <h2 class="text-base font-bold tracking-tight text-gray-900">Erneut übermitteln</h2>

    <div class="mt-4">
      <label class="mb-2 block text-sm font-bold tracking-tight text-gray-700" for="correction-reason">
        Grund der erneuten Übermittlung
      </label>
      <textarea id="correction-reason" rows="3" [attr.maxlength]="reasonMax" required
                aria-describedby="correction-reason-note"
                [ngModel]="reason()" (ngModelChange)="reason.set($event)"
                class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm
                       transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                       focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"></textarea>
      <p id="correction-reason-note" class="mt-2 flex flex-wrap justify-between gap-2 text-xs text-gray-500">
        <span>Der Grund wird mit jedem zurückgezogenen Kassenabschluss gespeichert.</span>
        <span>{{ reason().length }} / {{ reasonMax }}</span>
      </p>
    </div>

    <div class="mt-5 flex flex-wrap items-center gap-3">
      <button type="button"
              class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5
                     font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all duration-200
                     hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                     disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale"
              [disabled]="!canStart()" (click)="askToStart()">
        {{ creating() ? 'Wird gestartet …' : 'Erneut übermitteln' }}
      </button>
      @if (startHint(); as hint) {
        <span class="text-xs font-semibold text-gray-600" data-testid="correction-start-hint">{{ hint }}</span>
      }
    </div>

    <!-- The result of the last start attempt, directly under the button that caused it (Bug #866). -->
    @if (startResult(); as text) {
      <p class="mt-3 rounded-xl px-4 py-3 text-sm" data-testid="correction-start-result"
         [class.bg-green-50]="startResultIsSuccess()"
         [class.text-green-700]="startResultIsSuccess()"
         [class.bg-red-50]="!startResultIsSuccess()"
         [class.text-red-700]="!startResultIsSuccess()"
         [attr.role]="startResultIsSuccess() ? 'status' : 'alert'">{{ text }}</p>
    }
  </section>

  <!-- ── Bisherige Übermittlungen ───────────────────────────────────────────────────────────────────── -->
  <section class="flex flex-col gap-3" data-testid="correction-runs-section">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2 class="text-base font-bold tracking-tight text-gray-900">Bisherige Übermittlungen</h2>
      @if (live()) {
        <span class="inline-flex items-center gap-1.5 text-xs text-gray-500">
          <span class="h-2 w-2 rounded-full bg-green-500" aria-hidden="true"></span>
          Aktualisiert sich automatisch
        </span>
      }
    </div>

    <!-- The result of the last per-closing retry, at the top of the section it happened in (Bug #866). -->
    @if (retryResult(); as text) {
      <p class="rounded-xl px-4 py-3 text-sm" data-testid="correction-retry-result"
         [class.bg-green-50]="retryResultIsSuccess()"
         [class.text-green-700]="retryResultIsSuccess()"
         [class.bg-red-50]="!retryResultIsSuccess()"
         [class.text-red-700]="!retryResultIsSuccess()"
         [attr.role]="retryResultIsSuccess() ? 'status' : 'alert'">{{ text }}</p>
    }

    @if (runsLoading() && runs().length === 0) {
      <p class="text-sm text-gray-500">Übermittlungen werden geladen …</p>
    } @else if (runs().length === 0) {
      <p class="rounded-2xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        Es wurde noch keine erneute Übermittlung gestartet.
      </p>
    } @else {
      @for (run of runs(); track run.id) {
        <legal-closing-correction-run-card
          [run]="run"
          [detail]="openRunId() === run.id ? openRun() : null"
          [expanded]="openRunId() === run.id"
          [retryingItemId]="retryingItemId()"
          (toggled)="toggleRun($event)"
          (retry)="retryItem($event)"/>
      }
    }
  </section>
</section>

@if (confirming()) {
  <legal-closing-correction-confirm-dialog
    [count]="selectedCount()"
    [reason]="trimmedReason()"
    [busy]="creating()"
    (confirmed)="start()"
    (cancelled)="cancelStart()"/>
}
